import { Check } from 'lucide-react';
import { OrderCandidate } from '@/supabase/deposit/types/deposit';
import { cn } from '@/lib/utils';

const won = (n: number) => n.toLocaleString('ko-KR');

// 입금액과 후보 expectedAmounts 중 가장 가까운 값의 차액(원). |차이| 반환.
function amountDiff(c: OrderCandidate, amount: number): number {
  let best = Infinity;
  for (const a of c.expectedAmounts) best = Math.min(best, Math.abs(a - amount));
  return best === Infinity ? 0 : best;
}

interface CandidatePickerProps {
  candidates: OrderCandidate[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  nameMatchedIds?: Set<number>;   // 이 id들은 입금자명과 일치(금액은 불일치) → amber 배지
  depositAmount?: number;          // 배지의 금액 차이 계산용
}

export function CandidatePicker({
  candidates,
  selectedId,
  onSelect,
  nameMatchedIds,
  depositAmount,
}: CandidatePickerProps) {
  return (
    <div role="radiogroup" aria-label="입금 매칭 후보 주문" className="flex flex-col gap-2">
      {candidates.map((candidate) => {
        const selected = candidate.id === selectedId;
        return (
          <div
            key={candidate.id}
            role="radio"
            aria-checked={selected}
            tabIndex={0}
            onClick={() => onSelect(candidate.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(candidate.id);
              }
            }}
            className={cn(
              'flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
              selected
                ? 'border-primary bg-primary/5 ring-1 ring-primary'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            )}
          >
            <span
              aria-hidden
              className={cn(
                'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
                selected
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-gray-300'
              )}
            >
              {selected ? <Check className="h-3 w-3" /> : null}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className="font-semibold text-gray-900">{candidate.nickname}</span>
                <span className="text-xs text-gray-500">{candidate.order_date}</span>
                <span className="text-sm font-medium tabular-nums text-gray-900">
                  ₩{candidate.total_price.toLocaleString('ko-KR')}
                </span>
                {nameMatchedIds?.has(candidate.id) && (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">
                    이름 일치{depositAmount != null ? ` · 금액 ${won(amountDiff(candidate, depositAmount))}원 차이` : ''}
                  </span>
                )}
                {candidate.remainingAmount !== null && candidate.remainingAmount > 0 && (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">
                    잔금 {won(candidate.remainingAmount)}원
                  </span>
                )}
              </div>
              <p className="mt-0.5 truncate text-sm text-muted-foreground">
                {candidate.items_summary}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
